import type { EventRoute } from '@/shared/infrastructure/events/eventRouter';
import { EVENT_TYPES } from '@/shared/infrastructure/events/eventTypes';
import { storyKeys } from '@/contexts/stories/queryKeys';
import { illustrationKeys } from '@/contexts/illustrations/queryKeys';
import { audioStoryKeys } from '@/contexts/audio-stories/queryKeys';
import { mediaKeys } from '@/contexts/media/queryKeys';
import { pipelineKeys } from './queryKeys';

const EXPORT_KINDS = new Set(['pdf', 'print', 'video', 'youtube']);

/**
 * Query keys a v2 layer event refreshes. Every 📱 event of the pipeline is a
 * `pipeline.layer.updated` (layer moves, step journal, per-page / per-sheet /
 * per-block notices): the order tiles re-read, the stale view re-reads, and the
 * entity the layer writes (story text, the illustration's pages, the audio
 * blocks, the export files) is re-read so a page or a block shows up the
 * moment it lands.
 */
export function pipelineEventKeys(payload: Record<string, unknown>): Array<readonly unknown[]> {
  const storyId = typeof payload.storyId === 'string' ? payload.storyId : null;
  if (!storyId) return [];
  const orderId = typeof payload.orderId === 'string' ? payload.orderId : null;
  const keys: Array<readonly unknown[]> = [
    [...pipelineKeys.orderLayers(storyId)],
    [...pipelineKeys.stale(storyId)],
    // the story list's «Создаётся…» badge
    [...pipelineKeys.running()],
  ];
  if (orderId) keys.push([...pipelineKeys.order(orderId)]);
  const kind = typeof payload.kind === 'string' ? payload.kind : null;
  const entityId = typeof payload.entityId === 'string' ? payload.entityId : null;
  // a step event carries no kind: refresh what the story shows
  if (!kind || kind === 'story') {
    keys.push([...storyKeys.detail(storyId)]);
    // the title and the first block land with the text
    keys.push([...storyKeys.lists()]);
  }
  if (!kind || kind === 'illustration') {
    keys.push([...illustrationKeys.byStory(storyId)]);
    if (entityId && kind === 'illustration') keys.push([...illustrationKeys.detail(entityId)]);
  }
  if (!kind || kind === 'narration') {
    keys.push([...audioStoryKeys.lists()]);
    // the workspace reads the story's narrations under their own key (useAudioStoriesByStory)
    keys.push([...audioStoryKeys.all, 'byStory', storyId]);
    if (entityId && kind === 'narration') keys.push([...audioStoryKeys.detail(entityId)]);
  }
  if (!kind || EXPORT_KINDS.has(kind)) keys.push([...mediaKeys.exports(storyId)]);
  return keys;
}

/** A 📱 notice of an order (`pipeline.order.notice`): the order and its files re-read. */
export function pipelineNoticeKeys(payload: Record<string, unknown>): Array<readonly unknown[]> {
  const storyId = typeof payload.storyId === 'string' ? payload.storyId : null;
  if (!storyId) return [];
  const orderId = typeof payload.orderId === 'string' ? payload.orderId : null;
  const notice = typeof payload.notice === 'string' ? payload.notice : null;
  const keys: Array<readonly unknown[]> = [
    [...pipelineKeys.orderLayers(storyId)],
    [...pipelineKeys.running()],
    [...mediaKeys.exports(storyId)],
  ];
  if (orderId) keys.push([...pipelineKeys.order(orderId)]);
  // the thing the notice is about lands in its own rows
  if (notice === 'text_ready') keys.push([...storyKeys.detail(storyId)], [...storyKeys.lists()]);
  if (notice === 'page_ready' || notice === 'sheet_ready') keys.push([...illustrationKeys.byStory(storyId)]);
  if (notice === 'block_ready') keys.push([...audioStoryKeys.all, 'byStory', storyId]);
  return keys;
}

/** A v2 order layer moved → the workspace re-reads the story's order layers and what they write. */
export function registerPipelineEvents(
  register: (eventType: string, route: EventRoute) => void,
): void {
  register(EVENT_TYPES.PIPELINE.LAYER_UPDATED, { getQueryKeys: pipelineEventKeys });
  // the toast itself is OrderNoticeToaster (a local listener on the same event)
  register(EVENT_TYPES.PIPELINE.ORDER_NOTICE, { getQueryKeys: pipelineNoticeKeys });
}
